import { useCallback, useEffect, useRef } from 'react';
import { FormatListBulletedOutlined } from '@mui/icons-material';
import { useGetSoilAnalysisById } from '@practium/features/api';
import {
	ConfirmModal,
	FormActions,
	FormCard,
	FormField,
	PageHeader,
	TabBar,
	useConfirmModal,
	useToast,
} from '@practium/ui';
import type { CorrectionStartResponse, PlantingAdvice } from '../types';
import { CalagemTab } from '../components/Tabs/CalagemTab';
import { FosfatagemTab } from '../components/Tabs/FosfatagemTab';
import { GessagemTab } from '../components/Tabs/GessagemTab';
import { RecommendationTab } from '../components/Tabs/RecommendationTab';
import { LaudoTab } from '../components/Tabs/LaudoTab';
import { useCorrectionStore } from '../store/correctionStore';
import {
	CORRECTION_SIDEBAR_ITEMS,
	CORRECTION_TITLES,
	CORRECTION_SUBTITLES,
} from '../constants/correction.constants';
import { PanelActions } from '../components/PanelActions/PanelActions';
import { RecommendationSubtitle } from '../components/RecommendationSubtitle/RecommendationSubtitle';
import styles from './CorrectionWorkspacePage.module.css';

export interface CorrectionWorkspacePageProps {
	adviceId: string;
	viewData: PlantingAdvice;
	onBack: () => void;
	onEdit?: () => void;
	onDelete?: () => Promise<void> | void;
	canEditTabs?: boolean;
	startResult?: CorrectionStartResponse;
}

const getDisplayValue = (value: unknown): string => {
	if (!value) return '';
	if (typeof value === 'string') return value;
	if (typeof value === 'object') {
		const record = value as Record<string, unknown>;
		const label = record.name ?? record.title ?? record._id ?? record.id;
		return typeof label === 'string' ? label : '';
	}
	return String(value);
};

const getReferenceId = (value: unknown): string => {
	if (typeof value === 'string') return value;
	if (!value || typeof value !== 'object') return '';
	const record = value as Record<string, unknown>;
	const id = record._id ?? record.id;
	return typeof id === 'string' ? id : '';
};

const getReferenceName = (value: unknown): string => {
	if (!value || typeof value !== 'object') return '';
	const record = value as Record<string, unknown>;
	const name = record.name ?? record.title;
	return typeof name === 'string' ? name : '';
};

const GeneralPanel = ({
	viewData,
	onEdit,
	onDelete,
	onDeleteConfirm,
	soilAnalysisName,
}: {
	viewData: PlantingAdvice;
	onEdit?: () => void;
	onDelete?: () => void;
	onDeleteConfirm: () => void;
	soilAnalysisName: string;
}) => (
	<div className={styles.panelContent}>
		<FormCard icon={<FormatListBulletedOutlined />} title="Informações Gerais">
			<div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
				<div className={styles.row2}>
					<FormField label="Identificação">
						<span className={styles.viewText}>{viewData.name || '—'}</span>
					</FormField>
					<FormField label="Propriedade">
						<span className={styles.viewText}>
							{viewData.propName || getDisplayValue(viewData.prop) || '—'}
						</span>
					</FormField>
				</div>
				<div className={styles.row2}>
					<FormField label="Área">
						<span className={styles.viewText}>
							{viewData.areaName || getDisplayValue(viewData.area) || '—'}
						</span>
					</FormField>
					<FormField label="Setores">
						<span className={styles.viewText}>
							{viewData.sectors.map(getDisplayValue).filter(Boolean).join(', ') || '—'}
						</span>
					</FormField>
				</div>
				<div className={styles.halfLeft}>
					<FormField label="Análise de solo da área">
						<span className={styles.viewText}>{soilAnalysisName}</span>
					</FormField>
				</div>
			</div>
		</FormCard>
		<PanelActions>
			<FormActions
				mode="view"
				onCancel={() => {}}
				onSave={() => {}}
				onDelete={onDelete ? onDeleteConfirm : undefined}
				onEdit={onEdit}
			/>
		</PanelActions>
	</div>
);

export const CorrectionWorkspacePage = ({
	adviceId,
	viewData,
	onBack,
	onEdit,
	onDelete,
	canEditTabs = true,
	startResult,
}: CorrectionWorkspacePageProps) => {
	const { activeSidebarItem, setActiveSidebarItem, reset, preloadFromAdvice } =
		useCorrectionStore();
	const initializedAdviceIdRef = useRef<string | null>(null);

	useEffect(() => {
		const isNewAdvice = initializedAdviceIdRef.current !== adviceId;
		if (isNewAdvice) {
			reset();
			preloadFromAdvice(viewData, startResult);
			initializedAdviceIdRef.current = adviceId;
			return;
		}

		if (!useCorrectionStore.getState().hasUnsavedChanges) {
			preloadFromAdvice(viewData, startResult);
		}
	}, [adviceId, preloadFromAdvice, reset, startResult, viewData]);

	const {
		confirm,
		isOpen: isConfirmOpen,
		options: confirmOptions,
		handleConfirm,
		handleCancel: handleConfirmCancel,
	} = useConfirmModal();

	const toast = useToast();
	const embeddedSoilAnalysisName =
		viewData.soilAnalysisName || getReferenceName(viewData.soilAnalysis);
	const soilAnalysisId = getReferenceId(viewData.soilAnalysis);
	const { data: soilAnalysis, isLoading: isLoadingSoilAnalysis } = useGetSoilAnalysisById(
		soilAnalysisId,
		{ enabled: !embeddedSoilAnalysisName }
	);
	const soilAnalysisName =
		embeddedSoilAnalysisName ||
		soilAnalysis?.name ||
		(isLoadingSoilAnalysis ? 'Carregando...' : '—');

	const handleDelete = useCallback(async () => {
		const confirmed = await confirm({
			title: 'Excluir recomendação',
			message: 'Tem certeza que deseja excluir esta recomendação? Esta ação não pode ser desfeita.',
			confirmText: 'Excluir',
			cancelText: 'Cancelar',
			variant: 'danger',
		});
		if (confirmed) {
			try {
				await onDelete?.();
				toast.success('Recomendação excluída com sucesso.');
			} catch {
				toast.error('Erro ao excluir recomendação.');
			}
		}
	}, [confirm, onDelete, toast]);

	const renderPanel = (): React.ReactNode => {
		switch (activeSidebarItem) {
			case 'general':
				return (
					<GeneralPanel
						viewData={viewData}
						onEdit={onEdit}
						onDelete={onDelete}
						onDeleteConfirm={handleDelete}
						soilAnalysisName={soilAnalysisName}
					/>
				);
			case 'calagem':
				return <CalagemTab canEdit={canEditTabs} />;
			case 'fosfatagem':
				return <FosfatagemTab canEdit={canEditTabs} />;
			case 'gessagem':
				return <GessagemTab canEdit={canEditTabs} />;
			case 'recommendation':
				return <RecommendationTab canEdit={canEditTabs} />;
			case 'laudo':
				return <LaudoTab adviceId={adviceId} />;
			default:
				return null;
		}
	};

	return (
		<div className={styles.page}>
			<div className={styles.contentWrapper}>
				<PageHeader
					title={CORRECTION_TITLES['view']}
					subtitle={
						<RecommendationSubtitle text={CORRECTION_SUBTITLES['view']} property={viewData.prop} />
					}
					onBack={onBack}
				/>
				<div className={styles.workspace}>
					<div className={styles.workspaceTabs}>
						<TabBar
							tabs={CORRECTION_SIDEBAR_ITEMS}
							activeTab={activeSidebarItem}
							onTabChange={setActiveSidebarItem}
						/>
					</div>
					<div className={styles.workspaceContent}>{renderPanel()}</div>
				</div>
			</div>

			<ConfirmModal
				isOpen={isConfirmOpen}
				title={confirmOptions.title}
				message={confirmOptions.message}
				confirmText={confirmOptions.confirmText}
				cancelText={confirmOptions.cancelText}
				variant={confirmOptions.variant}
				onConfirm={handleConfirm}
				onCancel={handleConfirmCancel}
			/>
		</div>
	);
};
